/**
 * InicioAdminV2 - Admin Dashboard with KPIs, adoption chart, and contributions.
 * Design from Lovable, logic from chirigota.
 */
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
	Building2,
	Users,
	Wallet,
	CalendarClock,
	TrendingUp,
	ShieldCheck,
	Heart,
	PiggyBank,
	Award,
	Sparkles,
	ChevronDown,
	Info,
	RefreshCw,
	UserPlus
} from 'lucide-react';
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip as RechartsTooltip,
	Legend,
	ResponsiveContainer
} from 'recharts';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { PageHeader } from '../../../components/ui/page-header';
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger
} from '../../../components/ui/collapsible';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger
} from '../../../components/ui/tooltip';
import { ArcaContext } from '../../../context/ArcaContext.context';
import { I18nContext } from '../../../context/translations/TranslationsContext.context';
import { formatArcaNumber } from '../../../utils/formatArcaNumbers';
import getPensionPlanPerformance from '../../../api/calculators/getPensionPlanPerformance';
import getOnboardingStatus from '../../../api/companies/getOnboardingStatus';
import getAdminMonthlyContributionsByYear from '../../../api/calculators/getAdminMonthlyContributionsByYear';
import getCompanyInfoEppConfig from '../../../api/companies/getCompanyInfoEppConfig';
import { cn } from '../../../lib/utils';

interface ChartDataPoint {
	month: string;
	empresa: number;
	empleados: number;
}

const fadeUp = {
	hidden: { opacity: 0, y: 16 },
	visible: (i: number) => ({
		opacity: 1,
		y: 0,
		transition: { delay: i * 0.08, duration: 0.4, ease: [0, 0, 0.58, 1] as const }
	})
};

interface InicioAdminV2Props {
	userName?: string;
	onNavigateToPlantilla?: () => void;
	onNavigateToContributions?: () => void;
	onOpenInviteModal?: () => void;
}

const InicioAdminV2: React.FC<InicioAdminV2Props> = ({
	userName,
	onNavigateToPlantilla,
	onNavigateToContributions,
	onOpenInviteModal
}) => {
	const { token, companyId } = useContext(ArcaContext);
	const {
		state: { translate }
	} = useContext(I18nContext);

	const [isLoading, setIsLoading] = useState(true);
	const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
	const [holdingOpen, setHoldingOpen] = useState(false);
	const [companyName, setCompanyName] = useState('');

	// KPI data
	const [companyContributions, setCompanyContributions] = useState(0);
	const [employeeContributions, setEmployeeContributions] = useState(0);
	const [totalSaved, setTotalSaved] = useState(0);
	const [returnPct, setReturnPct] = useState(0);
	const [avgVoluntary, setAvgVoluntary] = useState(0);
	const [nextContribution, setNextContribution] = useState(0);
	const [nextContribPromoter, setNextContribPromoter] = useState(0);
	const [nextContribParticipants, setNextContribParticipants] = useState(0);
	const [nextContribDate, setNextContribDate] = useState('');

	// Onboarding data
	const [totalEmployees, setTotalEmployees] = useState(0);
	const [onboarded, setOnboarded] = useState(0);
	const [pending, setPending] = useState(0);
	const [declined, setDeclined] = useState(0);

	// Chart data
	const [chartData, setChartData] = useState<ChartDataPoint[]>([]);

	// Multi-company (holding) data
	const [isHolding, setIsHolding] = useState(false);
	const [groupCompanies, setGroupCompanies] = useState<Array<{ id: string; name: string }>>([]);
	const availableYears = useMemo(() => {
		const currentYear = new Date().getFullYear();
		return [currentYear, currentYear - 1].filter((y) => y >= 2024);
	}, []);

	// Chart loading state (separate from main loading)
	const [isChartLoading, setIsChartLoading] = useState(false);

	// Initial data load (KPIs, onboarding, company config)
	useEffect(() => {
		const fetchInitialData = async () => {
			if (!token || !companyId) return;
			setIsLoading(true);

			try {
				const [performanceRes, onboardingRes, companyConfigRes] = await Promise.all([
					getPensionPlanPerformance(token),
					getOnboardingStatus(token),
					getCompanyInfoEppConfig(token, companyId)
				]);

				// Process performance data
				/**
				 * @warning Backend bugs in POST /calculators/pension-plan-performance
				 * See src/docs/admin-kpi-backend-fixes.md for details.
				 *
				 * BROKEN values (hidden from UI, still fetched):
				 * - totalSavingsNow: includes ex-employees → avg per active employee is inflated
				 * - planPerformance.percentage: backend SUMS individual % instead of weighted avg → absurd value (N × individual return)
				 * - totalParticipantsContributions: is ANNUAL total, but was labeled as "monthly" → avgVoluntary also wrong
				 *
				 * CORRECT values (still displayed):
				 * - totalCompanyContributions, nextContribution, totalSavingsNow (raw total is OK)
				 */
				let empContribs = 0;
				if (performanceRes.success && performanceRes.data) {
					const data = performanceRes.data;
					setCompanyContributions(data.totalCompanyContributions || 0);
					empContribs = data.totalParticipantsContributions || 0;
					setEmployeeContributions(empContribs);
					setTotalSaved(data.totalSavingsNow || 0);
					setReturnPct(data.planPerformance?.percentage || 0);
					const promoter = data.nextContribution?.promoterAmount || 0;
					const participants = data.nextContribution?.participantsAmount || 0;
					setNextContribution(promoter + participants);
					setNextContribPromoter(promoter);
					setNextContribParticipants(participants);
					setNextContribDate(data.nextContribution?.nextContributionDate || '');
				}

				// Process onboarding data
				if (onboardingRes.success && onboardingRes.data) {
					const data = onboardingRes.data;
					const total = data.totalCompanyUsers || 0;
					const completed = data.completed || 0;
					const declinedCount = data.benefitDiscarded || 0;
					const pendingCount = Math.max(0, total - completed - declinedCount);
					setTotalEmployees(total);
					setOnboarded(completed);
					setPending(pendingCount);
					setDeclined(declinedCount);
					if (completed > 0 && empContribs > 0) {
						setAvgVoluntary(Math.round(empContribs / completed));
					}
				}

				// Process company config for holding
				if (companyConfigRes.success && companyConfigRes.data) {
					const allCompanies = companyConfigRes.data.allCompanies || [];
					setIsHolding(allCompanies.length > 1);
					setGroupCompanies(
						allCompanies.map((c: { id: string; name: string }) => ({
							id: c.id,
							name: c.name
						}))
					);
					const currentCompany = allCompanies.find((c: { id: string }) => c.id === companyId);
					if (currentCompany) {
						setCompanyName(currentCompany.name);
					}
				}
			} catch (error) {
				console.error('Error fetching admin dashboard data:', error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchInitialData();
	}, [token, companyId]);

	// Chart data load (only when year changes)
	useEffect(() => {
		const fetchChartData = async () => {
			if (!token) return;
			setIsChartLoading(true);

			try {
				const contributionsRes = await getAdminMonthlyContributionsByYear(token, selectedYear);
				if (contributionsRes.success && contributionsRes.data) {
					const monthLabels = Array.from({ length: 12 }, (_, i) =>
						translate(`admin.contributions.monthsShort.${i}`)
					);
					const { aportacionesNetasEmpresa, aportacionesNetasEmpleados, totalMonths } =
						contributionsRes.data;
					// Use totalMonths to only show months with data (like the old code)
					const months = totalMonths || 12;
					const formattedData = monthLabels.slice(0, months).map((month, index) => ({
						month,
						empresa: aportacionesNetasEmpresa[index] || 0,
						empleados: aportacionesNetasEmpleados[index] || 0
					}));
					setChartData(formattedData);
				}
			} catch (error) {
				console.error('Error fetching chart data:', error);
			} finally {
				setIsChartLoading(false);
			}
		};

		fetchChartData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token, selectedYear]);

	const adoptionRate = totalEmployees > 0 ? Math.round((onboarded / totalEmployees) * 100) : 0;
	const chartTotalEmpresa = chartData.reduce((sum, d) => sum + d.empresa, 0);
	const chartTotalEmpleados = chartData.reduce((sum, d) => sum + d.empleados, 0);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-20">
				<div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
			</div>
		);
	}

	return (
		<motion.div initial="hidden" animate="visible" className="space-y-6 sm:space-y-8">
			{/* Cabecera — la pone `ui/PageHeader`, puerto de Proa v0.10.0. El saludo va en
			    `eyebrow`, que existe en el componente precisamente por las dos homes: es un `<p>`
			    y no un encabezado, para que no compita con el `h1`.
			    `width="full"` porque esta pantalla NO tenía contenedor y va a todo el ancho; darle
			    el ancho por defecto estrecharía la rejilla de métricas (decisión de Marisa,
			    29/09/26). */}
			<motion.div custom={0} variants={fadeUp}>
				<PageHeader
					width="full"
					eyebrow={`${translate('admin.home.hello')} 👋`}
					title={
						<>
							<span className="text-primary">{userName || companyName}</span>,{' '}
							{translate('admin.home.allInOrder')}
						</>
					}
					subtitle={
						<>
							{translate('admin.home.companyRepresentative')} ·{' '}
							{translate('admin.home.allInOrderBadge')}
						</>
					}
				/>
			</motion.div>

			{/* KPI Metrics */}
			<TooltipProvider>
				<section>
					<h2 className="text-lg font-semibold text-foreground mb-4">
						{translate('admin.home.planSummary')}
					</h2>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
						{/* Company contributions */}
						<motion.div custom={1} variants={fadeUp}>
							<Card className="border-none shadow-xs h-full">
								<CardContent className="p-4 sm:p-5 relative">
									<Tooltip>
										<TooltipTrigger asChild>
											<button className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
												<Info className="w-4 h-4" />
											</button>
										</TooltipTrigger>
										<TooltipContent>
											<p className="text-xs max-w-[200px]">
												{translate('admin.home.tooltipCompanyContributions')}
											</p>
										</TooltipContent>
									</Tooltip>
									<div className="flex items-center gap-2 mb-3">
										<div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
											<Building2 className="w-4 h-4 text-primary" />
										</div>
									</div>
									<p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
										{translate('admin.home.cardTotalCompanyContributed')}
									</p>
									<p className="text-2xl sm:text-3xl font-bold text-primary">
										{formatArcaNumber(companyContributions)} €
									</p>
									{/* TODO: companyContributions is cumulative total, not monthly. See admin-kpi-backend-fixes.md */}
									{/* <p className="text-xs text-muted-foreground mt-2">
										{formatArcaNumber(companyContributions / Math.max(onboarded, 1))} €/mes{' '}
										{translate('admin.home.perEmployee')}
									</p> */}
								</CardContent>
							</Card>
						</motion.div>

						{/* Employee contributions */}
						<motion.div custom={2} variants={fadeUp}>
							<Card className="border-none shadow-xs h-full">
								<CardContent className="p-4 sm:p-5 relative">
									<Tooltip>
										<TooltipTrigger asChild>
											<button className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
												<Info className="w-4 h-4" />
											</button>
										</TooltipTrigger>
										<TooltipContent>
											<p className="text-xs max-w-[200px]">
												{translate('admin.home.tooltipEmployeeContributions')}
											</p>
										</TooltipContent>
									</Tooltip>
									<div className="flex items-center gap-2 mb-3">
										<div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
											<Users className="w-4 h-4 text-primary" />
										</div>
									</div>
									<p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
										{translate('admin.home.cardTotalParticipantsContributed')}
									</p>
									<p className="text-2xl sm:text-3xl font-bold text-primary">
										{formatArcaNumber(employeeContributions)} €
									</p>
									{/* TODO: avgVoluntary is broken — totalParticipantsContributions is annual, not monthly. See admin-kpi-backend-fixes.md */}
									{/* <p className="text-xs text-muted-foreground mt-2">
										{translate('admin.home.average')}: {formatArcaNumber(avgVoluntary)} €/mes
									</p> */}
								</CardContent>
							</Card>
						</motion.div>

						{/* Total saved */}
						<motion.div custom={3} variants={fadeUp}>
							<Card className="border-none shadow-xs h-full">
								<CardContent className="p-4 sm:p-5 relative">
									<Tooltip>
										<TooltipTrigger asChild>
											<button className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
												<Info className="w-4 h-4" />
											</button>
										</TooltipTrigger>
										<TooltipContent>
											<p className="text-xs max-w-[200px]">
												{translate('admin.home.tooltipTotalSaved')}
											</p>
										</TooltipContent>
									</Tooltip>
									<div className="flex items-center gap-2 mb-3">
										<div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
											<Wallet className="w-4 h-4 text-primary" />
										</div>
									</div>
									<p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
										{translate('admin.home.cardPlanPerformance')}
									</p>
									<p className="text-2xl sm:text-3xl font-bold text-primary">
										{formatArcaNumber(totalSaved)} €
									</p>
									{/* TODO: returnPct is broken — backend sums % instead of weighted avg. See admin-kpi-backend-fixes.md */}
									{/* <div className="flex items-center gap-1 mt-2">
										<TrendingUp className="w-3.5 h-3.5 text-primary" />
										<span className="text-xs font-medium text-primary">
											+{returnPct.toFixed(1)}% {translate('admin.home.performance')}
										</span>
									</div> */}
								</CardContent>
							</Card>
						</motion.div>

						{/* Next contribution */}
						<motion.div custom={4} variants={fadeUp}>
							<Card className="border-none shadow-xs h-full">
								<CardContent className="p-4 sm:p-5 relative">
									<Tooltip>
										<TooltipTrigger asChild>
											<button className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
												<Info className="w-4 h-4" />
											</button>
										</TooltipTrigger>
										<TooltipContent>
											<p className="text-xs max-w-[200px]">
												{translate('admin.home.tooltipNextContribution')}
											</p>
										</TooltipContent>
									</Tooltip>
									<div className="flex items-center gap-2 mb-3">
										<div className="w-9 h-9 rounded-lg bg-secondary/10 flex items-center justify-center">
											<CalendarClock className="w-4 h-4 text-secondary" />
										</div>
									</div>
									<p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
										{translate('admin.home.cardNextContribution')}
									</p>
									<p className="text-2xl sm:text-3xl font-bold text-secondary">
										{formatArcaNumber(nextContribution)} €
									</p>
									{nextContribDate && (
										<p className="text-xs text-muted-foreground mt-2">{nextContribDate}</p>
									)}
									<p className="text-[10px] text-muted-foreground mt-1">
										{translate('admin.home.company')}: {formatArcaNumber(nextContribPromoter)} € ·{' '}
										{translate('admin.home.employees')}: {formatArcaNumber(nextContribParticipants)}{' '}
										€
									</p>
								</CardContent>
							</Card>
						</motion.div>
					</div>
				</section>
			</TooltipProvider>

			{/* Holding breakdown - only show if multi-company */}
			{isHolding && groupCompanies.length > 1 && (
				<motion.section custom={5} variants={fadeUp}>
					<Collapsible open={holdingOpen} onOpenChange={setHoldingOpen}>
						<Card className="border-none shadow-xs bg-primary/5">
							<CardContent className="p-4">
								<CollapsibleTrigger asChild>
									<button
										type="button"
										className="w-full flex items-center justify-between cursor-pointer group"
									>
										<div className="flex items-center gap-2">
											<Building2 className="w-5 h-5 text-primary" />
											<h2 className="text-lg font-semibold text-foreground">
												{translate('admin.home.holdingBreakdown')}
											</h2>
											<span className="text-xs text-muted-foreground">
												· {groupCompanies.length} {translate('admin.home.companiesCount')}
											</span>
										</div>
										<ChevronDown
											className={cn(
												'w-5 h-5 text-muted-foreground transition-transform duration-200',
												holdingOpen && 'rotate-180'
											)}
										/>
									</button>
								</CollapsibleTrigger>

								{/* Summary totals */}
								<div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center mt-3">
									<div>
										<p className="text-[11px] text-muted-foreground mb-0.5">
											{translate('admin.home.totalEmployees')}
										</p>
										<p className="text-lg font-bold text-foreground">{totalEmployees}</p>
									</div>
									<div>
										<p className="text-[11px] text-muted-foreground mb-0.5">
											{translate('admin.home.totalSaved')}
										</p>
										<p className="text-lg font-bold text-primary">
											{formatArcaNumber(totalSaved)} €
										</p>
									</div>
									<div>
										<p className="text-[11px] text-muted-foreground mb-0.5">
											{translate('admin.home.averageAdoption')}
										</p>
										<p className="text-lg font-bold text-foreground">{adoptionRate}%</p>
									</div>
									{/* TODO: returnPct is broken. See admin-kpi-backend-fixes.md */}
									{/* <div>
										<p className="text-[11px] text-muted-foreground mb-0.5">
											{translate('admin.home.performance')}
										</p>
										<p className="text-lg font-bold text-primary">+{returnPct.toFixed(1)}%</p>
									</div> */}
								</div>
							</CardContent>
						</Card>

						<CollapsibleContent>
							<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
								{groupCompanies.map((company, i) => (
									<motion.div
										key={company.id}
										initial={{ opacity: 0, y: 8 }}
										animate={{ opacity: 1, y: 0 }}
										transition={{ delay: 0.1 * i, duration: 0.3 }}
									>
										<Card className="border-none shadow-xs hover:shadow-md transition-shadow">
											<CardContent className="p-5">
												<div className="flex items-center gap-2.5 mb-4">
													<div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
														<Building2 className="w-4 h-4 text-primary" />
													</div>
													<div className="min-w-0">
														<p className="text-sm font-semibold text-foreground truncate">
															{company.name}
														</p>
													</div>
												</div>
											</CardContent>
										</Card>
									</motion.div>
								))}
							</div>
						</CollapsibleContent>
					</Collapsible>
				</motion.section>
			)}

			{/* Employee Adoption - 2 column layout */}
			<motion.section custom={6} variants={fadeUp}>
				<div className="flex items-center justify-between mb-4">
					<h2 className="text-lg font-semibold text-foreground">
						{translate('admin.home.cardOnBoardingStatus')}
					</h2>
					<Button
						variant="ghost"
						size="sm"
						className="text-primary"
						onClick={onNavigateToPlantilla}
					>
						{translate('admin.home.viewPlantilla')} →
					</Button>
				</div>
				<div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
					{/* Left: Adoption stats (3 cols) */}
					<Card className="border-none shadow-xs lg:col-span-3">
						<CardContent className="p-4 sm:p-6">
							{/* Big percentage */}
							<div className="flex items-center gap-3 mb-4">
								<div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
									<Users className="w-5 h-5 text-primary" />
								</div>
								<div>
									<p className="text-3xl font-bold text-foreground">{adoptionRate}%</p>
									<p className="text-xs text-muted-foreground">
										{translate('admin.home.adoptionLabel')}
									</p>
								</div>
							</div>

							{/* Progress bar */}
							<div className="mb-5">
								{/* El testid no es para un test: lo usa la red visual para excluir esta barra, cuyos
								    anchos salen de los recuentos de empleados y cambian con cualquier alta.
								    Ver e2e/pantallas.ts, entrada de admin-inicio. */}
								<div
									data-testid="adoption-progress"
									className="h-3 bg-muted rounded-full overflow-hidden flex"
								>
									<div
										className="h-full bg-primary transition-all duration-500"
										style={{ width: `${(onboarded / Math.max(totalEmployees, 1)) * 100}%` }}
									/>
									<div
										className="h-full bg-amber-400 transition-all duration-500"
										style={{ width: `${(pending / Math.max(totalEmployees, 1)) * 100}%` }}
									/>
									<div
										className="h-full bg-secondary transition-all duration-500"
										style={{ width: `${(declined / Math.max(totalEmployees, 1)) * 100}%` }}
									/>
								</div>
							</div>

							{/* Stats boxes */}
							<div className="grid grid-cols-3 gap-3">
								<div className="bg-muted/30 rounded-lg p-3 text-center">
									<Users className="w-4 h-4 text-primary mx-auto mb-1" />
									<p className="text-xl font-bold text-foreground">{onboarded}</p>
									<p className="text-[10px] uppercase tracking-wide text-muted-foreground">
										{translate('admin.home.active')}
									</p>
								</div>
								<div className="bg-muted/30 rounded-lg p-3 text-center">
									<Users className="w-4 h-4 text-amber-500 mx-auto mb-1" />
									<p className="text-xl font-bold text-foreground">{pending}</p>
									<p className="text-[10px] uppercase tracking-wide text-muted-foreground">
										{translate('admin.home.pending')}
									</p>
								</div>
								<div className="bg-muted/30 rounded-lg p-3 text-center">
									<Users className="w-4 h-4 text-secondary mx-auto mb-1" />
									<p className="text-xl font-bold text-foreground">{declined}</p>
									<p className="text-[10px] uppercase tracking-wide text-muted-foreground">
										{translate('admin.home.notJoined')}
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* Right: CTA card (2 cols) */}
					<Card className="border-none shadow-xs bg-muted/30 lg:col-span-2">
						<CardContent className="p-4 sm:p-6 flex flex-col justify-between h-full">
							<div>
								<div className="flex items-center gap-2 mb-2">
									<RefreshCw className="w-4 h-4 text-secondary" />
									<p className="text-sm font-semibold text-foreground">
										{translate('admin.home.employeesNotJoined')}
									</p>
								</div>
								<p className="text-sm text-muted-foreground mb-4">
									{translate('admin.home.employeesNotJoinedDescription').replace(
										'{count}',
										String(pending + declined)
									)}
								</p>
							</div>
							<div className="space-y-2">
								<Button
									className="w-full bg-secondary hover:bg-secondary/90 text-white rounded-full"
									onClick={onOpenInviteModal}
								>
									<RefreshCw className="w-4 h-4 mr-2" />
									{translate('admin.home.reinvite')} ({pending + declined})
								</Button>
								<Button
									variant="outline"
									className="w-full rounded-full"
									onClick={onNavigateToPlantilla}
								>
									<UserPlus className="w-4 h-4 mr-2" />
									{translate('admin.home.managePlantilla')}
								</Button>
							</div>
						</CardContent>
					</Card>
				</div>
			</motion.section>

			{/* Contributions Chart */}
			<motion.section custom={7} variants={fadeUp}>
				{/* 🔴 `flex-wrap` y `gap`: sin ellos la fila no podía envolver y «Ver detalle →» se
				    salía 4,2 px de la pantalla en móvil, arrastrando el documento entero a 394 px
				    y descolocando las barras fijas al hacer scroll lateral.
				    Cabía con Roboto y dejó de caber al pasar el texto corrido a Inter (26/09/26),
				    que es más ancha. O sea que el margen era de milímetros y nadie lo sabía: no
				    había ninguna comprobación de desbordamiento. */}
				<div className="flex flex-wrap items-center justify-between gap-2 mb-4">
					<div className="flex items-center gap-3">
						<h2 className="text-lg font-semibold text-foreground">
							{translate('admin.home.monthlyContributions')} {selectedYear}
						</h2>
						{/* Year selector pills */}
						<div className="flex items-center gap-1 bg-muted/50 rounded-full p-1">
							{availableYears.map((year) => (
								<button
									key={year}
									onClick={() => setSelectedYear(year)}
									className={cn(
										'px-3 py-1 rounded-full text-xs font-medium transition-all',
										selectedYear === year
											? 'bg-primary text-white'
											: 'text-muted-foreground hover:text-foreground'
									)}
								>
									{year}
								</button>
							))}
						</div>
					</div>
					<Button
						variant="ghost"
						size="sm"
						className="text-primary"
						onClick={onNavigateToContributions}
					>
						{translate('admin.home.viewDetail')} →
					</Button>
				</div>
				<Card className="border-none shadow-xs">
					<CardContent className="p-4 sm:p-6">
						{/* Totals inline */}
						<div className="flex items-center gap-6 mb-6">
							<div>
								<p className="text-xs text-muted-foreground">
									{translate('admin.home.totalCompany')}
								</p>
								<p className="text-lg font-bold text-primary">
									{formatArcaNumber(chartTotalEmpresa)} €
								</p>
							</div>
							<div>
								<p className="text-xs text-muted-foreground">
									{translate('admin.home.totalEmployees')}
								</p>
								<p className="text-lg font-bold text-secondary">
									{formatArcaNumber(chartTotalEmpleados)} €
								</p>
							</div>
						</div>

						{/* Chart */}
						<div className="h-[300px] w-full relative">
							{isChartLoading && (
								<div className="absolute inset-0 flex items-center justify-center bg-background/50 z-10">
									<div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
								</div>
							)}
							<ResponsiveContainer width="100%" height="100%">
								<BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
									<CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
									<XAxis
										dataKey="month"
										tick={{ fontSize: 11, fill: '#9ca3af' }}
										axisLine={false}
										tickLine={false}
									/>
									<YAxis
										tick={{ fontSize: 11, fill: '#9ca3af' }}
										tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v))}
										axisLine={false}
										tickLine={false}
									/>
									<RechartsTooltip
										contentStyle={{
											backgroundColor: 'white',
											border: '1px solid #e5e7eb',
											borderRadius: '8px',
											fontSize: '12px'
										}}
										formatter={(value) => [`${formatArcaNumber(value as number)} €`]}
									/>
									<Legend wrapperStyle={{ fontSize: '11px' }} />
									<Bar
										dataKey="empresa"
										name={translate('admin.home.evolutionLabelEmployerContributions')}
										fill="#0D8044"
										radius={[4, 4, 0, 0]}
									/>
									<Bar
										dataKey="empleados"
										name={translate('admin.home.evolutionLabelEmployeesContributions')}
										fill="#F73469"
										radius={[4, 4, 0, 0]}
									/>
								</BarChart>
							</ResponsiveContainer>
						</div>
					</CardContent>
				</Card>
			</motion.section>

			{/* TODO: Impact section hidden — KPIs are broken. See admin-kpi-backend-fixes.md */}
			{false && (
				<motion.section custom={8} variants={fadeUp}>
					<div className="flex items-center gap-2 mb-4">
						<Heart className="w-5 h-5 text-secondary" />
						<h2 className="text-lg font-semibold text-foreground">
							{translate('admin.home.impactOf')} {companyName}
						</h2>
					</div>

					<Card className="border-none shadow-xs bg-linear-to-br from-primary/5 via-background to-secondary/5">
						<CardContent className="p-6">
							<div className="flex items-start gap-4 mb-5">
								<div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
									<Sparkles className="w-5 h-5 text-primary" />
								</div>
								<div>
									<p className="text-sm text-foreground font-medium leading-relaxed">
										{translate('admin.home.impactDescription').replace(
											'{amount}',
											`${formatArcaNumber(totalSaved)} €`
										)}
									</p>
								</div>
							</div>

							{/* Impact metrics grid */}
							<div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
								<div className="bg-card/80 backdrop-blur-xs rounded-lg border border-border/50 p-3.5 text-center">
									<div className="flex justify-center mb-2">
										<div className="w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center">
											<PiggyBank className="w-4 h-4 text-primary" />
										</div>
									</div>
									<p className="text-xl font-bold text-foreground tracking-tight">
										{formatArcaNumber(totalSaved / Math.max(onboarded, 1))} €
									</p>
									<p className="text-[11px] font-medium text-muted-foreground mt-0.5 leading-tight">
										{translate('admin.home.averageSavingsPerEmployee')}
									</p>
								</div>
								<div className="bg-card/80 backdrop-blur-xs rounded-lg border border-border/50 p-3.5 text-center">
									<div className="flex justify-center mb-2">
										<div className="w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center">
											<TrendingUp className="w-4 h-4 text-primary" />
										</div>
									</div>
									<p className="text-xl font-bold text-foreground tracking-tight">
										+{returnPct.toFixed(1)}%
									</p>
									<p className="text-[11px] font-medium text-muted-foreground mt-0.5 leading-tight">
										{translate('admin.home.planPerformance')}
									</p>
								</div>
								<div className="bg-card/80 backdrop-blur-xs rounded-lg border border-border/50 p-3.5 text-center">
									<div className="flex justify-center mb-2">
										<div className="w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center">
											<Award className="w-4 h-4 text-secondary" />
										</div>
									</div>
									<p className="text-xl font-bold text-foreground tracking-tight">
										{adoptionRate}%
									</p>
									<p className="text-[11px] font-medium text-muted-foreground mt-0.5 leading-tight">
										{translate('admin.home.activeEmployees')}
									</p>
								</div>
								<div className="bg-card/80 backdrop-blur-xs rounded-lg border border-border/50 p-3.5 text-center">
									<div className="flex justify-center mb-2">
										<div className="w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center">
											<Users className="w-4 h-4 text-secondary" />
										</div>
									</div>
									<p className="text-xl font-bold text-foreground tracking-tight">
										{formatArcaNumber(avgVoluntary)} €
									</p>
									<p className="text-[11px] font-medium text-muted-foreground mt-0.5 leading-tight">
										{translate('admin.home.averageMonthlyContribution')}
									</p>
								</div>
							</div>
						</CardContent>
					</Card>
				</motion.section>
			)}

			{/* Plan protection */}
			<motion.section custom={9} variants={fadeUp}>
				<Card className="border-none shadow-xs bg-primary/5">
					<CardContent className="p-5 flex items-start gap-4">
						<div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
							<ShieldCheck className="w-5 h-5 text-primary" />
						</div>
						<div>
							<h3 className="font-semibold text-sm mb-1">
								{translate('admin.home.planProtected')}
							</h3>
							<p className="text-sm text-muted-foreground">
								{translate('admin.home.planProtectedDescription')}
							</p>
						</div>
					</CardContent>
				</Card>
			</motion.section>

			{/* Footer - Partner logos */}
			<footer className="mt-12 pt-6 border-t border-border">
				<p className="text-xs text-muted-foreground text-center mb-4">
					{translate('admin.home.managedBy')}
				</p>
				<div className="flex items-center justify-center gap-8 flex-wrap">
					<img
						src="/imgs/ArcaLogo.png"
						alt="Arca"
						className="h-6 object-contain opacity-70 hover:opacity-100 transition-opacity"
					/>
					<img
						src="/imgs/caser-helvetia.png"
						alt="Caser Pensiones"
						className="h-6 object-contain opacity-70 hover:opacity-100 transition-opacity"
					/>
					<img
						src="/imgs/indexa-capital.png"
						alt="Indexa Capital"
						className="h-6 object-contain opacity-70 hover:opacity-100 transition-opacity"
					/>
					<img
						src="/imgs/cecabank-logo.png"
						alt="Cecabank"
						className="h-6 object-contain opacity-70 hover:opacity-100 transition-opacity"
					/>
				</div>
			</footer>
		</motion.div>
	);
};

export default InicioAdminV2;
